<template>
  <div
    v-bind="containerAttrs"
    :class="[ 'c-form-builder-list-field', { '-is-row': isRow } ]"
  >
    <component
      v-for="(itemValue, index) in usableValue"
      :is="getComponent(index)"
      ref="items"
      :key="index"
      :field-schema="itemsSchemas[index]"
      :model-value="itemValue"
      :list-actions="defineItemActions(modelValue, index)"
      :class="getFormFieldClasses(itemsSchemas[index])"
      :field-options="getOptions(index)"
      :row-field-options="fieldsOptions"
      :field-name="`${fieldName}-${index}`"
      :validation-field-name="`${fieldName}-${index}`"
      :form-value="formValue"
      :has-row-labels="index === 0"
      v-bind="fieldAttrs"
      @update:modelValue="onInput($event, modelValue, index)"
      @list:add="onAddField(modelValue, index, fieldSchema)"
      @list:remove="onRemoveField(modelValue, index, fieldSchema)"
      @pre-validate="$emit('pre-validate')"
      @keydown.enter.stop="onEnterKey(modelValue, index, fieldSchema, $event)"
    />
  </div>
</template>

<script>
import { is } from '@convenia/helpers'
import FormField from './FormField.next.vue'
import FormFieldRow from './FormFieldRow.next.vue'
import SubmitFormat from '../mixins/SubmitFormat'

/**
 * Only these input types works currently on item schema. That's because they are the ones
 * which follow the v-model structure
 */
const SUPPORTED_TYPES = [
  'text', 'select', 'color', 'check', 'radio', 'date', 'list-check', 'row'
]

const ROOT_ATTR_NAMES = [ 'class', 'style', 'id' ]

export default {
  name: 'CFormBuilderFormFieldList',

  inheritAttrs: false,

  mixins: [ SubmitFormat({ schemaRef: 'fieldSchema', childRefsName: 'items' }) ],

  components: {
    FormField,
    FormFieldRow
  },

  props: {
    modelValue: {
      required: true,
      type: Array
    },

    fieldSchema: {
      type: Object,
      required: true,
      validator (value) {
        return typeof (value || {}).itemSchema === 'function'
      }
    },

    fieldOptions: {
      type: Array,
      default: () => ([])
    },

    fieldsOptions: {
      type: Object,
      default: () => ({})
    },

    fieldName: {
      type: String,
      default: '',
    },

    formValue: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: [
    'update:modelValue',
    'list:add',
    'list:remove',
    'pre-validate',
  ],

  computed: {
    value () {
      return this.modelValue
    },

    containerAttrs () {
      return Object.entries(this.$attrs || {}).reduce((acc, [ key, value ]) => {
        if (
          ROOT_ATTR_NAMES.includes(key)
          || key.startsWith('data-')
          || key.startsWith('aria-')
        ) acc[key] = value

        return acc
      }, {})
    },

    fieldAttrs () {
      return Object.entries(this.$attrs || {}).reduce((acc, [ key, value ]) => {
        const isRootAttr = ROOT_ATTR_NAMES.includes(key)
          || key.startsWith('data-')
          || key.startsWith('aria-')

        if (!key.startsWith('on') && !isRootAttr) acc[key] = value

        return acc
      }, {})
    },

    isRow () {
      const { fieldSchema } = this
      const hasItemSchema = fieldSchema.hasOwnProperty('itemSchema')
      const isFunction = hasItemSchema && is(fieldSchema.itemSchema, 'Function')

      return isFunction && this.fieldSchema.itemSchema().type === 'row'
    },

    itemsSchemas () {
      return (this.modelValue || []).map((_, index) => {
        const itemSchema = this.fieldSchema.itemSchema(index)
        const { type } = itemSchema || {}
        if (!SUPPORTED_TYPES.includes(type)) console.warn(`
          [Form Builder list Input]: Unsupported item schema of type '${type}'
          Supported types: ${SUPPORTED_TYPES.map(v => `'${v}'`).join(', ')}.
          (field-index: ${index})
        `)

        return itemSchema
      })
    },

    usableValue () {
      return this.modelValue.slice(0, this.fieldSchema.maxLength || this.modelValue.length)
    }
  },

  methods: {
    getComponent (index) {
      return this.itemsSchemas?.[index]?.type === 'row'
        ? 'form-field-row'
        : 'form-field'
    },

    defineItemActions (value, index) {
      const itemSchema = this.itemsSchemas[index]
      const { noRemove, noAdd, hideActions } = itemSchema || {}
      if (hideActions) return { add: false, remove: false }

      const hasOptionsCount = this.fieldSchema.selectOnce
        && this.modelValue.length === this.fieldOptions.length

      return value.length > 1
        ? { add: hasOptionsCount ? false : !noAdd, remove: !noRemove }
        : { add: !noAdd }
    },

    getFormFieldClasses (itemSchema) {
      return [
        'form-field',
        {
          '-no-label': !itemSchema.label,
          '-hide-list-actions': itemSchema.hideActions
        }
      ]
    },

    getOptions (index) {
      const { trackBy, type } = this.itemsSchemas[index]
      const { selectOnce } = this.fieldSchema || {}
      const value = this.modelValue[index]
      const listTypes = [ 'select', 'check', 'radio', 'list-check' ]

      if (!listTypes.includes(type)) return []

      return selectOnce
        ? this.fieldOptions.filter(
          option => !this.modelValue.includes(option[trackBy]) || option[trackBy] === value)
        : this.fieldOptions
    },

    onInput (itemValue, value, index) {
      const newValue = [ ...(value || []) ]

      newValue[index] = itemValue
      this.$emit('update:modelValue', newValue)
    },

    onAddField (value, index, fieldSchema) {
      const newIndex = index + 1
      const newValue = [ ...(value || []) ]
      const newItemValue = this.getNewItemValue(newIndex)

      newValue.splice(newIndex, 0, newItemValue)

      const handler = () => {
        this.$emit('update:modelValue', newValue)
        this.$nextTick(() => this.focusItem(newIndex))
      }

      if (((fieldSchema || {}).manualCall || {}).add)
        return this.$emit('list:add', {
          index: newIndex,
          newItemValue,
          handler
        })

      handler()
    },

    onRemoveField (value, index, fieldSchema) {
      const newValue = [ ...(value || []) ]
      const [ removedItemValue ] = newValue.splice(index, 1)

      const handler = () => this.$emit('update:modelValue', newValue)

      if (((fieldSchema || {}).manualCall || {}).remove)
        return this.$emit('list:remove', {
          value: removedItemValue,
          index,
          handler
        })

      handler()
    },

    focusItem (index) {
      try {
        const field = this.$refs.items[index]
        if (field?.fieldSchema?.type !== 'select') field?.focusField()
      } catch (e) { return e }
    },

    getRemainingOptions (index) {
      const { trackBy } = this.fieldSchema.itemSchema(index)
      const valueSet = new Set(this.modelValue)

      return this.fieldOptions
        .map(option => (option || {})[trackBy])
        .filter(option => !valueSet.has(option))
    },

    getNewItemValue (newIndex) {
      const remainingOptions = this.fieldSchema.selectOnce
        ? this.getRemainingOptions(newIndex)
        : []

      // for now row type doesn't support c-select rows
      if (this.isRow) {
        const { length } = this.fieldSchema.itemSchema().schemas
        return Array(length).fill('')
      }

      return remainingOptions.length === 1
        ? remainingOptions[0]
        : this.fieldSchema.itemSchema(newIndex).value || null
    },

    onEnterKey (item, index, fieldSchema, event) {
      event.preventDefault()
      this.onAddField(item, index, fieldSchema)
    }
  },
}
</script>

<style lang="scss" src="./styles/FormFieldList.shared.scss"></style>
